import { Set } from "immutable";
import isFunction from "lodash/isFunction";
import React, { useContext, ReactNode, useMemo } from "react";

import Feature from "./Feature";

const defaultContext = Set<Feature>(); // Empty features set

const FeaturesContext = React.createContext<Set<Feature>>(defaultContext);

const { Provider } = FeaturesContext;

export type FeaturesProviderRenderProp = (features: Set<Feature>) => ReactNode;

/**
 * Retrieve the enabled features set
 */
export const useFeatures = () => useContext(FeaturesContext);

type Props = {
    /**
     * The features set that are considered to be enabled
     */
    features: Set<Feature> | ((features: Set<Feature>) => Set<Feature>);
    children: ReactNode | FeaturesProviderRenderProp;
};

/**
 * Enable features in part of the app
 */
export const FeaturesProvider = ({ features, children }: Props) => {
    const parentFeatures = useFeatures();
    const featureSet = useMemo(() => {
        return isFunction(features) ? features(parentFeatures) : features;
    }, [features, parentFeatures]);

    return <Provider value={featureSet}>{children && isFunction(children) ? children(featureSet) : children}</Provider>;
};

export default FeaturesContext;
